import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import PasswordInput from '../components/PasswordInput';
import './SignIn.css';

/**
 * Password reset landing page — the redirect target from the Resend email
 * link (link is minted by the backend via supabase admin.generate_link).
 *
 * When the customer clicks the link:
 *   1. Supabase's auth/v1/verify endpoint validates the token
 *   2. Redirects back here with #access_token=...&refresh_token=...&type=recovery
 *   3. The Supabase JS SDK parses the hash and fires PASSWORD_RECOVERY
 *
 * If the fragment is malformed, expired, or missing (link opened after
 * expiry / already used), we show the "invalid link" state with a way
 * to request a fresh one.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('waiting'); // waiting | ready | done | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    let mounted = true;
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY') setPhase('ready');
    });
    // Fallback: if the hash was already consumed (e.g. hot reload) but a
    // session exists, allow the reset anyway.
    const t = setTimeout(async () => {
      if (!mounted) return;
      const { data } = await supabase.auth.getSession();
      setPhase(prev => (prev === 'waiting' ? (data.session ? 'ready' : 'invalid') : prev));
    }, 1400);
    return () => {
      mounted = false;
      clearTimeout(t);
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setPhase('done');
    // Sign out the recovery session so the user has to sign in with the new pw.
    await supabase.auth.signOut();
    setTimeout(() => navigate('/signin'), 1800);
  };

  return (
    <div className="auth">
      <div className="auth-l">
        <div className="streak" style={{ top: '30%', right: '-20px', width: '240px' }}></div>
        <div className="streak" style={{ top: 'calc(30% + 12px)', right: '-20px', width: '160px', opacity: '0.5' }}></div>
        <div className="brandrow">
          <img className="mark" src="/logos/instacut-mark-28.svg" alt="" width="28" height="28" />
          Instacut
        </div>
        <h3>One last step,<br />pick a strong one.</h3>
        <p>Choose a password you don&apos;t use anywhere else. Minimum 8 characters — mix in a number or symbol if you can.</p>
        <div className="quote">&ldquo;A password manager makes this painless — try 1Password, Bitwarden or Apple Keychain.&rdquo;</div>
      </div>

      <div className="auth-r">
        <h4>Set a new password</h4>
        <div className="s">Your reset link is only valid once. Take your time.</div>

        {phase === 'waiting' && (
          <div className="error-message" style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--mute)' }} data-testid="reset-waiting">
            Verifying your reset link&hellip;
          </div>
        )}

        {phase === 'invalid' && (
          <div data-testid="reset-invalid">
            <div className="error-message">
              <strong>This reset link is invalid or has expired.</strong>
              <div style={{ marginTop: 4, color: '#7f1d1d' }}>
                Links are valid for one hour and can only be used once.
              </div>
            </div>
            <Link
              to="/forgot"
              className="btn p"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', textDecoration: 'none' }}
              data-testid="reset-request-new"
            >
              Request a new link
            </Link>
            <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--mute)', marginTop: '18px' }}>
              <Link to="/signin" style={{ color: 'var(--acc)', fontWeight: 600, textDecoration: 'none' }}>
                <ArrowLeft size={12} style={{ verticalAlign: '-1px', marginRight: 2 }} />
                Back to sign in
              </Link>
            </div>
          </div>
        )}

        {phase === 'done' && (
          <div data-testid="reset-done">
            <div
              className="error-message"
              style={{ background: 'var(--paper)', borderColor: 'var(--acc)', color: 'var(--ink)', display: 'flex', alignItems: 'flex-start', gap: 10 }}
            >
              <Check size={18} color="var(--acc)" strokeWidth={3} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong>Password updated.</strong>
                <div style={{ marginTop: 4, color: 'var(--mute)' }}>
                  Redirecting you to sign in&hellip;
                </div>
              </div>
            </div>
          </div>
        )}

        {phase === 'ready' && (
          <form onSubmit={submit}>
            {error && (
              <div className="error-message" data-testid="reset-error">
                {error}
              </div>
            )}

            <div className="field">
              <label>New password</label>
              <PasswordInput
                required
                autoFocus
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="At least 8 characters"
                data-testid="reset-pw"
                toggleTestId="reset-pw-toggle"
              />
            </div>

            <div className="field">
              <label>Confirm password</label>
              <PasswordInput
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={loading}
                placeholder="Type it again"
                data-testid="reset-confirm"
                toggleTestId="reset-confirm-toggle"
              />
            </div>

            <button
              type="submit"
              className="btn p"
              disabled={loading || !password || !confirm}
              style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
              data-testid="reset-submit"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
